import { Component, type ErrorInfo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { buttonVariants } from "@/components/ui/button-variants";
import {
  chunkUrlFromError,
  isChunkFailure,
} from "@/engine/support/chunkFailure";

/**
 * A chunk failure gets a different report than a render error, because
 * `TypeError` is not a name anybody can act on.
 */
function reportError(error: Error, componentStack: string | null | undefined) {
  // The reporter is asked for here rather than imported: it owns PostHog, which
  // is the heaviest thing in the bundle, and this boundary wraps every page — a
  // static import puts it in front of every first paint (#531). Nothing is lost
  // by fetching it now: the fallback is already on screen, and a report a few
  // hundred milliseconds later is the same report.
  void import("@/engine/diagnostics/analytics")
    .then((analytics) => {
      if (isChunkFailure(error)) {
        analytics.captureChunkFailure(
          {
            chunk: chunkUrlFromError(error),
            path: window.location.pathname,
            phase: "runtime",
            attempts: 1,
            recovered: false,
          },
          error,
        );
        return;
      }
      analytics.captureReactError(error, componentStack);
    })
    .catch(() => {
      // The reporter's own chunk did not arrive — most likely the same network
      // that produced the failure being reported. Swallowed rather than left to
      // reject: the boot watchdog reloads the document on an unhandled
      // rejection that reads like a chunk failure, and this one would make a
      // render error reload the page under the visitor.
    });
}

interface Props {
  children: ReactNode;
}

/**
 * An `Error`, or a value shaped like one. A `DOMException` is an `Error` in a
 * browser but not in every runtime the boundary is exercised in, and it is
 * reported as itself rather than as a copy that lost its name.
 */
function isErrorLike(value: unknown): value is Error {
  return (
    value instanceof Error ||
    (typeof value === "object" &&
      value !== null &&
      "name" in value &&
      "message" in value)
  );
}

interface State {
  hasError: boolean;
  chunkFailed: boolean;
}

/**
 * Catches React render errors, which never reach `window.onerror`.
 *
 * It is also where a chunk failure ends up when nothing else could catch it.
 * `lazyChunk` covers the imports the application makes; the chunks the
 * framework imports as it hydrates are not among them, and an `import()` whose
 * rejection React takes over raises no `error` event and no
 * `unhandledrejection` for the boot watchdog to see. So it arrives here, and is
 * told apart from a render error by its message: a different screen, because
 * reloading is the remedy and "Something went wrong" does not say so.
 *
 * The reload stays the visitor's. A boundary that reloads on its own is a
 * boundary that can loop.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, chunkFailed: false };

  static getDerivedStateFromError(error: unknown): State {
    return { hasError: true, chunkFailed: isChunkFailure(error) };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error("React render error:", error);
    // Whatever was thrown is reported: `throw null` is a render error too, and
    // the report needs something with a message.
    reportError(
      isErrorLike(error) ? error : new Error(String(error)),
      info.componentStack,
    );
  }

  override render() {
    if (this.state.chunkFailed) return <ChunkFailure />;
    if (this.state.hasError) return <RenderFailure />;
    return this.props.children;
  }
}

/** The boundary's fallback; a class cannot read the catalogue itself. */
function RenderFailure() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
      <h1 className="text-lg font-semibold">{t("error.boundaryTitle")}</h1>
      <p className="text-muted-foreground text-sm">{t("error.boundaryBody")}</p>
      <Button size="sm" onClick={() => window.location.reload()}>
        {t("action.reload")}
      </Button>
    </div>
  );
}

/**
 * The screen for a chunk that never arrived. There is nothing to try again
 * here — the import that rejected belongs to the framework and is spent — so
 * the two ways out are a fresh document, which names chunks that exist, and the
 * start of the app.
 */
function ChunkFailure() {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      data-testid="app-chunk-failed"
      className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center"
    >
      <h1 className="text-lg font-semibold">{t("error.pageTitle")}</h1>
      <p className="text-muted-foreground text-sm">{t("error.boundaryBody")}</p>
      <div className="flex flex-wrap justify-center gap-2">
        <Button
          size="sm"
          data-testid="app-chunk-reload"
          onClick={() => window.location.reload()}
        >
          {t("action.reload")}
        </Button>
        <a
          className={buttonVariants({ variant: "outline", size: "sm" })}
          href="/"
        >
          {t("action.goHome")}
        </a>
      </div>
    </div>
  );
}

export default ErrorBoundary;
